import { InitiativeStatus, type Initiative } from '../../api/initiative-client/initiative-client.type';
import type { ProcessAnalysisInitiativeChartData } from '../../api/portfolio-client/portfolio-client.type';
import {
  Measure,
  TimeAllocationType,
  type MeasureMetadata,
  type TimeAllocation,
} from '../../api/work-periods-client/work-periods-client.type';
import {
  ComparisonMode,
  MetricType,
  MultipleViewMode,
  Tab,
  ViewMode,
} from '../../containers/process-analysis/process-analysis.type';
import { useGlobalStore } from '../global-store/global-store';
import { useProcessAnalysisStore } from './process-analysis-store';
import { getDefaultComparisonModeFilters } from './process-analysis-store.helpers';
import {
  Panel,
  type ComparisonModeFilters,
  type DataPoint,
  type EntityFilters,
  type FilterEntityKey,
  type InitiativeOption,
  type SelectedTarget,
  type SelectionScope,
  type State,
} from './process-analysis-store.type';

/**
 * Gets the current comparison mode
 *
 * @returns {ComparisonMode} The current comparison mode
 */
const getCurrentComparisonMode = (): ComparisonMode => {
  const portfolio = useGlobalStore.getState().portfolio;
  const state = useProcessAnalysisStore.getState();
  const { activeTab, selectedPortfolios, selectedTeams, selectedBoards } = state;

  switch (activeTab) {
    case Tab.Portfolios:
      return selectedPortfolios.length > 0 && portfolio?.id ? ComparisonMode.Multiple : ComparisonMode.Single;
    case Tab.Teams:
      return selectedTeams.length > 1 ? ComparisonMode.Multiple : ComparisonMode.Single;
    case Tab.Boards:
      return selectedBoards.length > 1 ? ComparisonMode.Multiple : ComparisonMode.Single;
    default:
      return ComparisonMode.Single;
  }
};

/**
 * Gets the current primary entity
 *
 * @returns {string | null} The current primary entity
 */
const getCurrentPrimaryEntity = (): string | null => {
  const portfolio = useGlobalStore.getState().portfolio;
  const state = useProcessAnalysisStore.getState();
  const { activeTab, selectedTeams, selectedBoards } = state;

  switch (activeTab) {
    case Tab.Portfolios:
      return portfolio?.id || null;
    case Tab.Teams:
      return selectedTeams[0] || null;
    case Tab.Boards:
      return selectedBoards[0] || null;
    default:
      return null;
  }
};

/**
 * Gets the current entity key
 *
 * @returns {FilterEntityKey | null} The current entity key
 */
const getCurrentEntityKey = (): FilterEntityKey | null => {
  const state = useProcessAnalysisStore.getState();
  const primaryEntity = getCurrentPrimaryEntity();

  if (!primaryEntity) {
    return null;
  }

  return `${state.activeTab}:${primaryEntity}` as FilterEntityKey;
};

/**
 * Sets the active tab in the Process Analysis store.
 *
 * @param {Tab} tab - The tab to set as active
 * @return {void}
 */
const setActiveTab = (tab: Tab): void => {
  useProcessAnalysisStore.setState({ activeTab: tab }, false, '[Process Analysis Store] setActiveTab');
  setViewMode(ViewMode.MeasureComparison);
  setMultipleViewMode(MultipleViewMode.MonthlyAverages);
  setActivePanel(Panel.Filters);
  setSelectedDataPoint(null);
};

/**
 * Sets the view mode in the Process Analysis store.
 *
 * @param {ViewMode} viewMode - The view mode to set
 * @return {void}
 */
const setViewMode = (viewMode: ViewMode): void => {
  useProcessAnalysisStore.setState({ viewMode }, false, '[Process Analysis Store] setViewMode');
};

/**
 * Sets the multiple view mode in the Process Analysis store.
 *
 * @param {MultipleViewMode} multipleViewMode - The multiple view mode to set
 * @return {void}
 */
const setMultipleViewMode = (multipleViewMode: MultipleViewMode): void => {
  useProcessAnalysisStore.setState({ multipleViewMode }, false, '[Process Analysis Store] setMultipleViewMode');
};

/**
 * Sets the active panel in the Process Analysis store.
 *
 * @param {Panel | null} panel - The panel to set, or null to clear
 * @return {void}
 */
const setActivePanel = (panel: Panel | null): void => {
  useProcessAnalysisStore.setState({ activePanel: panel }, false, '[Process Analysis Store] setActivePanel');
};

/**
 * Toggles the visibility of a series on the agent cycle time chart.
 *
 * @param {string} seriesKey - The key of the series to toggle (e.g. 'cycle_time', 'agent_cycle_time')
 * @returns {void}
 */
const toggleAgentSeriesVisibility = (seriesKey: string): void => {
  const current = useProcessAnalysisStore.getState().agentVisibleSeries;
  useProcessAnalysisStore.setState(
    { agentVisibleSeries: { ...current, [seriesKey]: !current[seriesKey] } },
    false,
    '[Process Analysis Store] toggleAgentSeriesVisibility',
  );
};

/**
 * Sets the active view mode for the agent dashboard (e.g. 'cycle_time', 'usage').
 *
 * @param {string} viewMode - The view mode to set
 * @returns {void}
 */
const setAgentViewMode = (viewMode: string): void => {
  // The Usage view's metrics (sessions/events/adoption/AI-code) are not
  // task-scoped, so the task type and story-point filters are hidden there.
  // Clear any lingering selection when switching to Usage so it doesn't
  // silently re-apply when the user returns to the Cycle Time view.
  useProcessAnalysisStore.setState(
    viewMode === 'usage'
      ? { agentViewMode: viewMode, selectedTaskType: null, selectedPoints: null }
      : { agentViewMode: viewMode },
    false,
    '[Process Analysis Store] setAgentViewMode',
  );
};

/**
 * Sets the chart display type for the agent dashboard (e.g. 'line', 'bar', 'histogram').
 *
 * @param {string} chartType - The chart type to set
 * @returns {void}
 */
const setAgentChartType = (chartType: string): void => {
  useProcessAnalysisStore.setState({ agentChartType: chartType }, false, '[Process Analysis Store] setAgentChartType');
};

/**
 * Sets the date range in the Process Analysis store.
 *
 * @param {string} startDate - The start date
 * @param {string} endDate - The end date
 * @return {void}
 */
const setDateRange = (startDate: string, endDate: string): void => {
  useProcessAnalysisStore.setState({ startDate, endDate }, false, '[Process Analysis Store] setDateRange');
};

/**
 * Sets the selected task type filter shared across all Process Analysis
 * tabs. Pass `null` to clear the filter (equivalent to "All work item types").
 *
 * @param {string | null} taskType - The normalized Task.type value (e.g. "bug"), or null to clear
 * @return {void}
 */
const setSelectedTaskType = (taskType: string | null): void => {
  useProcessAnalysisStore.setState(
    { selectedTaskType: taskType },
    false,
    '[Process Analysis Store] setSelectedTaskType',
  );
};

/**
 * Sets the selected story-point filter for the Agents-tab Cycle Time view.
 * Pass a number to filter to that estimate, `'__none__'` to filter to
 * unestimated tasks (points IS NULL), or `null` to clear the filter
 * (equivalent to "All story points").
 *
 * @param {number | '__none__' | null} points - The estimate to filter by, '__none__' for unestimated, or null to clear
 * @return {void}
 */
const setSelectedPoints = (points: number | '__none__' | null): void => {
  useProcessAnalysisStore.setState({ selectedPoints: points }, false, '[Process Analysis Store] setSelectedPoints');
};

/**
 * Sets the allocation type filter shared across all Process Analysis tabs.
 * Pass `null` to revert to the auto-computed value derived from the date range.
 *
 * @param {TimeAllocationType | null} allocationType - Explicit allocation type, or null for auto
 * @return {void}
 */
const setSelectedAllocationType = (allocationType: TimeAllocationType | null): void => {
  useProcessAnalysisStore.setState(
    { selectedAllocationType: allocationType },
    false,
    '[Process Analysis Store] setSelectedAllocationType',
  );
};

/**
 * Sets the available measures in the Process Analysis store.
 *
 * @param {MeasureMetadata[]} availableMeasures - The available measures to set
 * @return {void}
 */
const setAvailableMeasures = (availableMeasures: MeasureMetadata[]): void => {
  useProcessAnalysisStore.setState({ availableMeasures }, false, '[Process Analysis Store] setAvailableMeasures');
};

/**
 * Sets the time allocations data in the Process Analysis store.
 *
 * @param {TimeAllocation[]} timeAllocations - The time allocations data to set
 * @return {void}
 */
const setTimeAllocations = (timeAllocations: TimeAllocation[]): void => {
  useProcessAnalysisStore.setState({ timeAllocations }, false, '[Process Analysis Store] setTimeAllocations');
};

/**
 * Sets the initiatives data in the Process Analysis store.
 *
 * @param {Initiative[]} initiatives - The initiatives data to set
 * @return {void}
 */
const setInitiatives = (initiatives: Initiative[]): void => {
  useProcessAnalysisStore.setState({ initiatives }, false, '[Process Analysis Store] setInitiatives');
};

/**
 * Sets the selected initiative statuses for filtering.
 *
 * @param {InitiativeStatus[]} statuses - Array of initiative statuses to select
 * @return {void}
 */
const setSelectedInitiativeStatuses = (statuses: InitiativeStatus[]): void => {
  const comparisonMode = getCurrentComparisonMode();

  if (comparisonMode === ComparisonMode.Single) {
    updateCurrentEntityFilters({ selectedInitiativeStatuses: statuses });
  }
};

/**
 * Toggles an initiative status in the selection.
 *
 * @param {InitiativeStatus} status - The initiative status to toggle
 * @return {void}
 */
const toggleInitiativeStatusSelection = (status: InitiativeStatus): void => {
  const currentFilters = getCurrentEntityFilters();
  const currentSelected = currentFilters?.selectedInitiativeStatuses || [];

  if (currentSelected.includes(status)) {
    const updatedSelection = currentSelected.filter((s) => s !== status);
    setSelectedInitiativeStatuses(updatedSelection);
  } else {
    const updatedSelection = [...currentSelected, status];
    setSelectedInitiativeStatuses(updatedSelection);
  }
};

/**
 * Sets the initiative completion data in the Process Analysis store.
 *
 * @param {ProcessAnalysisInitiativeChartData | null} data - The initiative completion data to set
 * @return {void}
 */
const setInitiativeCompletionData = (data: ProcessAnalysisInitiativeChartData | null): void => {
  useProcessAnalysisStore.setState(
    { initiativeCompletionData: data },
    false,
    '[Process Analysis Store] setInitiativeCompletionData',
  );
};

/**
 * Sets the initiative focus data in the Process Analysis store.
 *
 * @param {ProcessAnalysisInitiativeChartData | null} data - The initiative focus data to set
 * @return {void}
 */
const setInitiativeFocusData = (data: ProcessAnalysisInitiativeChartData | null): void => {
  useProcessAnalysisStore.setState(
    { initiativeFocusData: data },
    false,
    '[Process Analysis Store] setInitiativeFocusData',
  );
};

/**
 * Sets the selected initiative completions in the Process Analysis store.
 *
 * @param {InitiativeOption[]} selectedInitiativeCompletions - The selected initiative completions to set
 * @return {void}
 */
const setSelectedInitiativeCompletions = (selectedInitiativeCompletions: InitiativeOption[]): void => {
  const comparisonMode = getCurrentComparisonMode();

  if (comparisonMode === ComparisonMode.Single) {
    updateCurrentEntityFilters({ selectedInitiativeCompletions });
  }
};

/**
 * Toggles an initiative completion selection (max 4 items).
 *
 * @param {InitiativeOption} initiative - The initiative to toggle
 * @return {void}
 */
const toggleInitiativeCompletionSelection = (initiative: InitiativeOption): void => {
  const currentFilters = getCurrentEntityFilters();
  const currentSelected = currentFilters?.selectedInitiativeCompletions || [];
  const currentTrends = currentFilters?.selectedInitiativeCompletionTrends || [];
  const isSelected = currentSelected.some((item) => item.id === initiative.id);

  if (isSelected) {
    const updatedSelection = currentSelected.filter((item) => item.id !== initiative.id);
    const updatedTrends = currentTrends.filter((item) => item.id !== initiative.id);

    updateCurrentEntityFilters({
      selectedInitiativeCompletions: updatedSelection,
      selectedInitiativeCompletionTrends: updatedTrends,
    });
  } else if (currentSelected.length < 4) {
    const updatedSelection = [...currentSelected, initiative];
    updateCurrentEntityFilters({
      selectedInitiativeCompletions: updatedSelection,
    });
  }
};

/**
 * Toggles an initiative completion trend selection.
 *
 * @param {InitiativeOption} initiative - The initiative to toggle trend for
 * @return {void}
 */
const toggleInitiativeCompletionTrends = (initiative: InitiativeOption): void => {
  const currentFilters = getCurrentEntityFilters();
  const currentSelected = currentFilters?.selectedInitiativeCompletions || [];
  const currentTrends = currentFilters?.selectedInitiativeCompletionTrends || [];
  const isInitiativeSelected = currentSelected.some((item) => item.id === initiative.id);
  const isTrendSelected = currentTrends.some((item) => item.id === initiative.id);

  if (isTrendSelected) {
    const updatedTrends = currentTrends.filter((item) => item.id !== initiative.id);
    updateCurrentEntityFilters({
      selectedInitiativeCompletionTrends: updatedTrends,
    });
  } else if (isInitiativeSelected) {
    const updatedTrends = [...currentTrends, initiative];
    updateCurrentEntityFilters({
      selectedInitiativeCompletionTrends: updatedTrends,
    });
  }
};

/**
 * Resets initiative completion filters to default state atomically.
 * Sets selectedInitiativeCompletions to the first four initiatives and clears trends.
 *
 * @param {InitiativeOption[]} initiatives - Available initiatives to select from
 * @return {void}
 */
const resetInitiativeCompletionFilters = (initiatives: InitiativeOption[]): void => {
  const comparisonMode = getCurrentComparisonMode();

  if (comparisonMode === ComparisonMode.Single) {
    updateCurrentEntityFilters({
      selectedInitiativeCompletions: initiatives.slice(0, 4),
      selectedInitiativeCompletionTrends: [],
    });
  }
};

/**
 * Resets initiative focus filters to default state atomically.
 * Sets selectedInitiativeFocus to the first four initiatives and clears trends.
 *
 * @param {InitiativeOption[]} initiatives - Available initiatives to select from
 * @return {void}
 */
const resetInitiativeFocusFilters = (initiatives: InitiativeOption[]): void => {
  const comparisonMode = getCurrentComparisonMode();

  if (comparisonMode === ComparisonMode.Single) {
    updateCurrentEntityFilters({
      selectedInitiativeFocus: initiatives.slice(0, 4),
      selectedInitiativeFocusTrends: [],
    });
  }
};

/**
 * Resets measure comparison filters to default state atomically.
 * Sets selectedMeasures to KEY_MEASURES and clears trends.
 *
 * @param {Measure[]} keyMeasures - The key measures to select by default
 * @return {void}
 */
const resetMeasureComparisonFilters = (keyMeasures: Measure[]): void => {
  const comparisonMode = getCurrentComparisonMode();

  if (comparisonMode === ComparisonMode.Single) {
    updateCurrentEntityFilters({
      selectedMeasures: keyMeasures,
      selectedMeasureTrends: [],
    });
  }
};

/**
 * Resets key measures over time filters to default state atomically.
 * Sets selectedMeasureOverTime to the first available measure and clears trend.
 *
 * @param {Measure} firstMeasure - The first available measure to select
 * @return {void}
 */
const resetKeyMeasuresOverTimeFilters = (firstMeasure: Measure): void => {
  const comparisonMode = getCurrentComparisonMode();
  const activeTab = useProcessAnalysisStore.getState().activeTab;

  if (comparisonMode === ComparisonMode.Multiple) {
    setComparisonModeFilters(activeTab, {
      selectedMeasureOverTime: firstMeasure,
      selectedMeasureOverTimeTrend: false,
    });
  }
};

/**
 * Sets the selected initiative focus in the Process Analysis store.
 *
 * @param {InitiativeOption[]} selectedInitiativeFocus - The selected initiative focus to set
 * @return {void}
 */
const setSelectedInitiativeFocus = (selectedInitiativeFocus: InitiativeOption[]): void => {
  const comparisonMode = getCurrentComparisonMode();

  if (comparisonMode === ComparisonMode.Single) {
    updateCurrentEntityFilters({ selectedInitiativeFocus });
  }
};

/**
 * Toggles an initiative focus selection (max 4 items).
 *
 * @param {InitiativeOption} initiative - The initiative to toggle
 * @return {void}
 */
const toggleInitiativeFocusSelection = (initiative: InitiativeOption): void => {
  const currentFilters = getCurrentEntityFilters();
  const currentSelected = currentFilters?.selectedInitiativeFocus || [];
  const currentTrends = currentFilters?.selectedInitiativeFocusTrends || [];
  const isSelected = currentSelected.some((item) => item.id === initiative.id);

  if (isSelected) {
    const updatedSelection = currentSelected.filter((item) => item.id !== initiative.id);
    const updatedTrends = currentTrends.filter((item) => item.id !== initiative.id);

    updateCurrentEntityFilters({
      selectedInitiativeFocus: updatedSelection,
      selectedInitiativeFocusTrends: updatedTrends,
    });
  } else if (currentSelected.length < 4) {
    const updatedSelection = [...currentSelected, initiative];
    updateCurrentEntityFilters({
      selectedInitiativeFocus: updatedSelection,
    });
  }
};

/**
 * Toggles an initiative focus trend selection.
 *
 * @param {InitiativeOption} initiative - The initiative to toggle trend for
 * @return {void}
 */
const toggleInitiativeFocusTrends = (initiative: InitiativeOption): void => {
  const currentFilters = getCurrentEntityFilters();
  const currentSelected = currentFilters?.selectedInitiativeFocus || [];
  const currentTrends = currentFilters?.selectedInitiativeFocusTrends || [];
  const isInitiativeSelected = currentSelected.some((item) => item.id === initiative.id);
  const isTrendSelected = currentTrends.some((item) => item.id === initiative.id);

  if (isTrendSelected) {
    const updatedTrends = currentTrends.filter((item) => item.id !== initiative.id);
    updateCurrentEntityFilters({
      selectedInitiativeFocusTrends: updatedTrends,
    });
  } else if (isInitiativeSelected) {
    const updatedTrends = [...currentTrends, initiative];
    updateCurrentEntityFilters({
      selectedInitiativeFocusTrends: updatedTrends,
    });
  }
};

/**
 * Resets filters for process analysis except entity filters - for the purpose of caching.
 *
 * @return {void}
 */
const resetFilters = (): void => {
  useProcessAnalysisStore.setState(
    {
      activeTab: Tab.Portfolios,
      viewMode: ViewMode.MeasureComparison,
      multipleViewMode: MultipleViewMode.MonthlyAverages,
      selectedPortfolios: [],
      selectedTeams: [],
      selectedBoards: [],
      selectedCapacityTeams: [],
      selectedTaskType: null,
      selectedPoints: null,
      selectedAllocationType: null,
      comparisonModeEntityFilters: getDefaultComparisonModeFilters(),
    },
    false,
    '[Process Analysis Store] resetFilters',
  );
};

/**
 * Filters a selection down to the ids that are still available in scope.
 *
 * Returns `undefined` when there is nothing to write — either the scope for this
 * entity type is unknown (its list has not resolved for the active portfolio, so
 * clearing against it would wipe valid state) or every selected id survived.
 *
 * @param {string[]} selectedIds - The currently selected entity ids
 * @param {Set<string> | undefined} availableIds - Ids still selectable, or undefined when unknown
 * @returns {string[] | undefined} The reduced selection, or undefined when no write is needed
 */
const dropUnavailableIds = (selectedIds: string[], availableIds?: Set<string>): string[] | undefined => {
  if (!availableIds) {
    return undefined;
  }

  const remainingIds = selectedIds.filter((id) => availableIds.has(id));

  return remainingIds.length === selectedIds.length ? undefined : remainingIds;
};

/**
 * Drops entity selections whose ids no longer exist in the currently active scope.
 *
 * Every entity selection is persisted to localStorage, so switching portfolio
 * would otherwise leave behind ids belonging to the previous portfolio: the
 * selector renders a phantom "1 Project" while no option in its dropdown is
 * checked, and every downstream query keeps asking for an entity outside the
 * active scope. Ids that DO exist in the new scope survive, so switching between
 * two portfolios that share a project keeps that project selected.
 *
 * Only the entity types present on `scope` are touched, and all surviving changes
 * are written in a single atomic `setState` so subscribers never observe a
 * half-pruned selection. Pruning is idempotent: re-running it against the same
 * scope writes nothing, so callers may safely re-run it on every selection change.
 *
 * `selectedCapacityTeams` is deliberately NOT pruned here. Its checkbox list is
 * built from `portfolio.projects`, which is a strict superset of the portfolio
 * projects endpoint this scope is derived from, so a project that is selectable
 * in that list but absent from the endpoint would have its selection silently
 * revoked.
 *
 * @param {SelectionScope} scope - Ids still selectable in the active scope, per entity type
 * @returns {void}
 */
const pruneSelectionsToScope = (scope: SelectionScope): void => {
  const { selectedPortfolios, selectedTeams, selectedBoards } = useProcessAnalysisStore.getState();

  const prunedPortfolios = dropUnavailableIds(selectedPortfolios, scope.portfolioIds);
  const prunedTeams = dropUnavailableIds(selectedTeams, scope.teamIds);
  const prunedBoards = dropUnavailableIds(selectedBoards, scope.boardIds);

  const updates: Partial<State> = {};

  if (prunedPortfolios) {
    updates.selectedPortfolios = prunedPortfolios;
  }

  if (prunedTeams) {
    updates.selectedTeams = prunedTeams;
  }

  if (prunedBoards) {
    updates.selectedBoards = prunedBoards;
  }

  if (Object.keys(updates).length === 0) {
    return;
  }

  useProcessAnalysisStore.setState(updates, false, '[Process Analysis Store] pruneSelectionsToScope');
};

/**
 * Sets the selected portfolios for comparison and automatically updates comparison mode.
 *
 * @param {string[]} portfolioIds - Array of portfolio IDs to compare against
 * @return {void}
 */
const setSelectedPortfolios = (portfolioIds: string[]): void => {
  useProcessAnalysisStore.setState(
    {
      selectedPortfolios: portfolioIds.slice(0, 3), // Limit to maximum 3 portfolios
    },
    false,
    '[Process Analysis Store] setSelectedPortfolios',
  );
};

/**
 * Sets the selected teams for comparison.
 *
 * @param {string[]} teamIds - Array of team IDs to select
 * @return {void}
 */
const setSelectedTeams = (teamIds: string[]): void => {
  useProcessAnalysisStore.setState(
    {
      selectedTeams: teamIds.slice(0, 4), // Limit to maximum 4 teams
    },
    false,
    '[Process Analysis Store] setSelectedTeams',
  );
};

/**
 * Sets the selected boards for comparison.
 *
 * @param {string[]} boardIds - Array of board IDs to select
 * @return {void}
 */
const setSelectedBoards = (boardIds: string[]): void => {
  useProcessAnalysisStore.setState(
    {
      selectedBoards: boardIds.slice(0, 4), // Limit to maximum 4 boards
    },
    false,
    '[Process Analysis Store] setSelectedBoards',
  );
};

/**
 * Sets the selected capacity teams.
 *
 * @param {string[]} teamIds - Array of team IDs to select for capacity analysis
 * @return {void}
 */
const setSelectedCapacityTeams = (teamIds: string[]): void => {
  const comparisonMode = getCurrentComparisonMode();
  const activeTab = useProcessAnalysisStore.getState().activeTab;

  if (comparisonMode === ComparisonMode.Single && activeTab === Tab.Portfolios) {
    // Single mode in Portfolios tab - store in entity filters
    updateCurrentEntityFilters({ selectedCapacityTeams: teamIds });
  } else if (comparisonMode === ComparisonMode.Multiple && activeTab === Tab.Teams) {
    // Multiple mode in Teams tab - store in global state
    useProcessAnalysisStore.setState(
      {
        selectedCapacityTeams: teamIds,
      },
      false,
      '[Process Analysis Store] setSelectedCapacityTeams',
    );
  }
};

/**
 * Gets the currently selected capacity teams from the appropriate source based on comparison mode and tab.
 * Mirrors the logic from useSelectedCapacityTeams hook for use in actions.
 *
 * @returns {string[]} Currently selected capacity team IDs
 */
const getCurrentSelectedCapacityTeams = (): string[] => {
  const state = useProcessAnalysisStore.getState();
  const comparisonMode = getCurrentComparisonMode();
  const activeTab = state.activeTab;

  if (comparisonMode === ComparisonMode.Single && activeTab === Tab.Portfolios) {
    const entityFilters = getCurrentEntityFilters();
    return entityFilters?.selectedCapacityTeams || [];
  } else if (comparisonMode === ComparisonMode.Multiple && activeTab === Tab.Teams) {
    return state.selectedCapacityTeams;
  }

  return [];
};

/**
 * Adds a team to the capacity teams selection.
 *
 * @param {string} teamId - Team ID to add
 * @return {void}
 */
const addCapacityTeamToSelection = (teamId: string): void => {
  const currentSelected = getCurrentSelectedCapacityTeams();

  if (!currentSelected.includes(teamId) && currentSelected.length < 4) {
    const updatedSelection = [...currentSelected, teamId];
    setSelectedCapacityTeams(updatedSelection);
  }
};

/**
 * Removes a team from the capacity teams selection.
 *
 * @param {string} teamId - Team ID to remove
 * @return {void}
 */
const removeCapacityTeamFromSelection = (teamId: string): void => {
  const currentSelected = getCurrentSelectedCapacityTeams();
  const updatedSelection = currentSelected.filter((id) => id !== teamId);
  setSelectedCapacityTeams(updatedSelection);
};

/**
 * Toggles a team in/out of the capacity teams selection.
 *
 * @param {string} teamId - Team ID to toggle
 * @return {void}
 */
const toggleCapacityTeamSelection = (teamId: string): void => {
  const currentSelected = getCurrentSelectedCapacityTeams();

  if (currentSelected.includes(teamId)) {
    removeCapacityTeamFromSelection(teamId);
  } else {
    addCapacityTeamToSelection(teamId);
  }
};

/**
 * Sets the selected measures in the Process Analysis store.
 *
 * @param {Measure[]} selectedMeasures - The selected measures to set
 * @return {void}
 */
const setSelectedMeasures = (selectedMeasures: Measure[]): void => {
  const comparisonMode = getCurrentComparisonMode();

  if (comparisonMode === ComparisonMode.Single) {
    updateCurrentEntityFilters({ selectedMeasures });
  }
  // Note: Multiple mode uses different views, so no global state update needed here
};

/**
 * Sets the selected measure trends in the Process Analysis store.
 *
 * @param {Measure[]} selectedMeasureTrends - The selected measure trends to set
 * @return {void}
 */
const setSelectedMeasureTrends = (selectedMeasureTrends: Measure[]): void => {
  const comparisonMode = getCurrentComparisonMode();

  if (comparisonMode === ComparisonMode.Single) {
    updateCurrentEntityFilters({ selectedMeasureTrends });
  }
};

/**
 * Adds a measure to the selection (up to 4 measures).
 *
 * @param {Measure} measure - The measure to add
 * @return {void}
 */
const addMeasureToSelection = (measure: Measure): void => {
  const currentFilters = getCurrentEntityFilters();
  const currentSelected = currentFilters?.selectedMeasures || [];

  if (!currentSelected.includes(measure) && currentSelected.length < 4) {
    const newSelection = [...currentSelected, measure];
    setSelectedMeasures(newSelection);
  }
};

/**
 * Removes a measure from the selection.
 *
 * @param {Measure} measure - The measure to remove
 * @return {void}
 */
const removeMeasureFromSelection = (measure: Measure): void => {
  const currentFilters = getCurrentEntityFilters();
  const currentSelected = currentFilters?.selectedMeasures || [];
  const currentTrends = currentFilters?.selectedMeasureTrends || [];

  const newSelection = currentSelected.filter((m) => m !== measure);
  const newTrends = currentTrends.filter((m) => m !== measure);

  setSelectedMeasures(newSelection);
  setSelectedMeasureTrends(newTrends);

  const currentTargets = useProcessAnalysisStore.getState().selectedTargets;
  const newTargets = currentTargets.filter((target) => target.measureId !== measure);
  setSelectedTargets(newTargets);
};

/**
 * Toggles a measure in the selection.
 *
 * @param {Measure} measure - The measure to toggle
 * @return {void}
 */
const toggleMeasureSelection = (measure: Measure): void => {
  const currentFilters = getCurrentEntityFilters();
  const currentSelected = currentFilters?.selectedMeasures || [];
  const isSelected = currentSelected.includes(measure);

  if (isSelected) {
    removeMeasureFromSelection(measure);
  } else {
    addMeasureToSelection(measure);
  }
};

/**
 * Toggles trends for a selected measure.
 *
 * @param {Measure} measure - The measure to toggle trends for
 * @return {void}
 */
const toggleMeasureTrends = (measure: Measure): void => {
  const currentFilters = getCurrentEntityFilters();
  const currentTrends = currentFilters?.selectedMeasureTrends || [];
  const hasTrends = currentTrends.includes(measure);

  if (hasTrends) {
    const newTrends = currentTrends.filter((m) => m !== measure);
    setSelectedMeasureTrends(newTrends);
  } else {
    const newTrends = [...currentTrends, measure];
    setSelectedMeasureTrends(newTrends);
  }
};

/**
 * Sets the selected monthly averages measures in the Process Analysis store.
 *
 * @param {Measure[]} selectedMonthlyAveragesMeasures - The selected monthly averages measures to set
 * @return {void}
 */
const setSelectedMonthlyAveragesMeasures = (selectedMonthlyAveragesMeasures: Measure[]): void => {
  const comparisonMode = getCurrentComparisonMode();
  const activeTab = useProcessAnalysisStore.getState().activeTab;

  if (comparisonMode === ComparisonMode.Multiple) {
    setComparisonModeFilters(activeTab, { selectedMonthlyAveragesMeasures });
  }
};

/**
 * Adds a measure to the monthly averages selection (no limit for monthly averages).
 *
 * @param {Measure} measure - The measure to add
 * @return {void}
 */
const addMonthlyAveragesMeasureToSelection = (measure: Measure): void => {
  const comparisonMode = getCurrentComparisonMode();
  const state = useProcessAnalysisStore.getState();

  if (comparisonMode === ComparisonMode.Multiple) {
    const currentSelected = getComparisonModeFilters(state.activeTab).selectedMonthlyAveragesMeasures;

    if (!currentSelected.includes(measure)) {
      const newSelection = [...currentSelected, measure];
      setSelectedMonthlyAveragesMeasures(newSelection);
    }
  }
};

/**
 * Removes a measure from the monthly averages selection.
 *
 * @param {Measure} measure - The measure to remove
 * @return {void}
 */
const removeMonthlyAveragesMeasureFromSelection = (measure: Measure): void => {
  const comparisonMode = getCurrentComparisonMode();
  const state = useProcessAnalysisStore.getState();

  if (comparisonMode === ComparisonMode.Multiple) {
    const currentSelected = getComparisonModeFilters(state.activeTab).selectedMonthlyAveragesMeasures;
    const newSelection = currentSelected.filter((m) => m !== measure);
    setSelectedMonthlyAveragesMeasures(newSelection);
  }
};

/**
 * Toggles a measure in the monthly averages selection.
 *
 * @param {Measure} measure - The measure to toggle
 * @return {void}
 */
const toggleMonthlyAveragesMeasureSelection = (measure: Measure): void => {
  const comparisonMode = getCurrentComparisonMode();
  const state = useProcessAnalysisStore.getState();

  if (comparisonMode === ComparisonMode.Multiple) {
    const currentSelected = getComparisonModeFilters(state.activeTab).selectedMonthlyAveragesMeasures;
    const isSelected = currentSelected.includes(measure);

    if (isSelected) {
      removeMonthlyAveragesMeasureFromSelection(measure);
    } else {
      addMonthlyAveragesMeasureToSelection(measure);
    }
  }
};

/**
 * Resets monthly averages filters to default state.
 * Clears all selected measures (sets to empty array).
 *
 * @return {void}
 */
const resetMonthlyAveragesFilters = (): void => {
  const comparisonMode = getCurrentComparisonMode();
  const activeTab = useProcessAnalysisStore.getState().activeTab;

  if (comparisonMode === ComparisonMode.Multiple) {
    setComparisonModeFilters(activeTab, { selectedMonthlyAveragesMeasures: [] });
  }
};

/**
 * Selects all available monthly averages measures.
 *
 * @param {Measure[]} allMeasures - All available measures to select
 * @return {void}
 */
const selectAllMonthlyAveragesMeasures = (allMeasures: Measure[]): void => {
  const comparisonMode = getCurrentComparisonMode();
  const activeTab = useProcessAnalysisStore.getState().activeTab;

  if (comparisonMode === ComparisonMode.Multiple) {
    setComparisonModeFilters(activeTab, { selectedMonthlyAveragesMeasures: allMeasures });
  }
};

/**
 * Sets the selected data point in the Process Analysis store.
 *
 * @param {DataPoint | null} dataPoint - The data point to set, or null to clear
 * @return {void}
 */
const setSelectedDataPoint = (dataPoint: DataPoint | null): void => {
  useProcessAnalysisStore.setState(
    { selectedDataPoint: dataPoint },
    false,
    '[Process Analysis Store] setSelectedDataPoint',
  );
};

/**
 * Sets the initiative completion metric in the Process Analysis store.
 *
 * @param {MetricType} metric - The metric type to set (Tasks or Points)
 * @return {void}
 */
const setInitiativeCompletionMetric = (metric: MetricType): void => {
  useProcessAnalysisStore.setState(
    { initiativeCompletionMetric: metric },
    false,
    '[Process Analysis Store] setInitiativeCompletionMetric',
  );
};

/**
 * Sets the initiative focus metric in the Process Analysis store.
 *
 * @param {MetricType} metric - The metric type to set (Tasks or Points)
 * @return {void}
 */
const setInitiativeFocusMetric = (metric: MetricType): void => {
  useProcessAnalysisStore.setState(
    { initiativeFocusMetric: metric },
    false,
    '[Process Analysis Store] setInitiativeFocusMetric',
  );
};

/**
 * Sets the selected measure for Key Measures Over Time view.
 *
 * @param {Measure | null} measure - The measure to set
 * @return {void}
 */
const setSelectedMeasureOverTime = (measure: Measure | null): void => {
  const comparisonMode = getCurrentComparisonMode();
  const activeTab = useProcessAnalysisStore.getState().activeTab;

  if (comparisonMode === ComparisonMode.Multiple) {
    setComparisonModeFilters(activeTab, { selectedMeasureOverTime: measure });
  }
};

/**
 * Sets the trend line visibility for the selected measure in Key Measures Over Time view.
 *
 * @param {boolean} showTrend - Whether to show the trend line
 * @return {void}
 */
const setSelectedMeasureOverTimeTrend = (showTrend: boolean): void => {
  const comparisonMode = getCurrentComparisonMode();
  const activeTab = useProcessAnalysisStore.getState().activeTab;

  if (comparisonMode === ComparisonMode.Multiple) {
    setComparisonModeFilters(activeTab, { selectedMeasureOverTimeTrend: showTrend });
  }
};

/**
 * Sets the selected targets in the Process Analysis store.
 *
 * @param {SelectedTarget[]} selectedTargets - The selected targets to set
 * @return {void}
 */
const setSelectedTargets = (selectedTargets: SelectedTarget[]): void => {
  useProcessAnalysisStore.setState({ selectedTargets }, false, '[Process Analysis Store] setSelectedTargets');
};

/**
 * Toggles a target in/out of the selection.
 *
 * @param {SelectedTarget} target - The target to toggle
 * @return {void}
 */
const toggleTargetSelection = (target: SelectedTarget): void => {
  const currentSelected = useProcessAnalysisStore.getState().selectedTargets;
  const isSelected = currentSelected.some(
    (selectedTarget) => selectedTarget.measureId === target.measureId && selectedTarget.targetId === target.targetId,
  );

  if (isSelected) {
    const updatedSelection = currentSelected.filter(
      (selectedTarget) =>
        !(selectedTarget.measureId === target.measureId && selectedTarget.targetId === target.targetId),
    );
    setSelectedTargets(updatedSelection);
  } else {
    const updatedSelection = [...currentSelected, target];
    setSelectedTargets(updatedSelection);
  }
};

/**
 * Gets the current entity filters for the primary entity in single mode
 *
 * @returns {EntityFilters | null} Current entity filters or null if no primary entity or not in single mode
 */
const getCurrentEntityFilters = (): EntityFilters | null => {
  const state = useProcessAnalysisStore.getState();
  const comparisonMode = getCurrentComparisonMode();
  const entityKey = getCurrentEntityKey();

  if (comparisonMode === ComparisonMode.Single && entityKey) {
    return state.singleModeEntityFilters[entityKey] || null;
  }

  return null;
};

/**
 * Updates the current entity filters for single mode
 *
 * @param {Partial<EntityFilters>} partialFilters - Partial filters to update
 * @return {void}
 */
const updateCurrentEntityFilters = (partialFilters: Partial<EntityFilters>): void => {
  const comparisonMode = getCurrentComparisonMode();
  const entityKey = getCurrentEntityKey();

  if (comparisonMode === ComparisonMode.Single && entityKey) {
    const currentState = useProcessAnalysisStore.getState();
    const currentEntityFilters = currentState.singleModeEntityFilters[entityKey] || {
      selectedMeasures: [],
      selectedMeasureTrends: [],
      selectedInitiativeStatuses: [],
      selectedInitiativeCompletions: [],
      selectedInitiativeCompletionTrends: [],
      selectedInitiativeFocus: [],
      selectedInitiativeFocusTrends: [],
      selectedCapacityTeams: [],
      touched: {
        selectedMeasures: false,
        selectedMeasureTrends: false,
        selectedInitiativeStatuses: false,
        selectedInitiativeCompletions: false,
        selectedInitiativeCompletionTrends: false,
        selectedInitiativeFocus: false,
        selectedInitiativeFocusTrends: false,
        selectedCapacityTeams: false,
      },
    };

    // Automatically mark touched fields based on what's being updated
    const currentTouched = currentEntityFilters.touched || {};
    const updatedTouched = { ...currentTouched };

    Object.keys(partialFilters).forEach((key) => {
      if (key !== 'touched' && key in updatedTouched) {
        updatedTouched[key as keyof NonNullable<EntityFilters['touched']>] = true;
      }
    });

    const updatedEntityFilters = {
      ...currentEntityFilters,
      ...partialFilters,
      touched: updatedTouched,
    };

    useProcessAnalysisStore.setState(
      {
        singleModeEntityFilters: {
          ...currentState.singleModeEntityFilters,
          [entityKey]: updatedEntityFilters,
        },
      },
      false,
      '[Process Analysis Store] updateCurrentEntityFilters',
    );
  }
};

/**
 * Gets the comparison mode filters for a specific tab.
 *
 * @param {Tab} tab - The tab to get filters for
 * @returns {ComparisonModeFilters} The filters for the specified tab
 */
const getComparisonModeFilters = (tab: Tab): ComparisonModeFilters => {
  const state = useProcessAnalysisStore.getState();
  return state.comparisonModeEntityFilters[tab];
};

/**
 * Updates comparison mode filters for a specific tab.
 *
 * @param {Tab} tab - The tab to update filters for
 * @param {Partial<ComparisonModeFilters>} partialFilters - Partial filters to update
 * @return {void}
 */
const setComparisonModeFilters = (tab: Tab, partialFilters: Partial<ComparisonModeFilters>): void => {
  const currentState = useProcessAnalysisStore.getState();
  const currentFilters = currentState.comparisonModeEntityFilters[tab];

  const updatedFilters = {
    ...currentFilters,
    ...partialFilters,
  };

  useProcessAnalysisStore.setState(
    {
      comparisonModeEntityFilters: {
        ...currentState.comparisonModeEntityFilters,
        [tab]: updatedFilters,
      },
    },
    false,
    '[Process Analysis Store] setComparisonModeFilters',
  );
};

export {
  pruneSelectionsToScope,
  resetFilters,
  resetInitiativeCompletionFilters,
  resetInitiativeFocusFilters,
  resetKeyMeasuresOverTimeFilters,
  resetMeasureComparisonFilters,
  resetMonthlyAveragesFilters,
  selectAllMonthlyAveragesMeasures,
  setActivePanel,
  setActiveTab,
  setAgentChartType,
  setAgentViewMode,
  setAvailableMeasures,
  setDateRange,
  setInitiativeCompletionData,
  setInitiativeCompletionMetric,
  setInitiativeFocusData,
  setInitiativeFocusMetric,
  setInitiatives,
  setMultipleViewMode,
  setSelectedAllocationType,
  setSelectedBoards,
  setSelectedCapacityTeams,
  setSelectedDataPoint,
  setSelectedInitiativeCompletions,
  setSelectedInitiativeFocus,
  setSelectedInitiativeStatuses,
  setSelectedMeasureOverTime,
  setSelectedMeasureOverTimeTrend,
  setSelectedMeasures,
  setSelectedMonthlyAveragesMeasures,
  setSelectedPortfolios,
  setSelectedTeams,
  setSelectedTaskType,
  setSelectedPoints,
  setTimeAllocations,
  setViewMode,
  toggleAgentSeriesVisibility,
  toggleCapacityTeamSelection,
  toggleInitiativeCompletionSelection,
  toggleInitiativeCompletionTrends,
  toggleInitiativeFocusSelection,
  toggleInitiativeFocusTrends,
  toggleInitiativeStatusSelection,
  toggleMeasureSelection,
  toggleMeasureTrends,
  toggleMonthlyAveragesMeasureSelection,
  toggleTargetSelection,
};
